import { TrustGraphPanel } from '@/components/trust-graph-panel';
import { useCurrentUser } from '@/hooks/use-auth';
import { useSyncState, useSyncTrigger } from '@/hooks/use-sync';
import { get } from '@/lib/api';
import {
	ANALYSIS_PATH_COLORS,
	ANALYSIS_PATH_LABELS,
	SYNC_STATUS_COLORS,
	SYNC_STATUS_LABELS,
	THREAT_CLASS_LABELS,
	formatRelativeTime,
} from '@/lib/constants';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import type { PostureRepository, PostureSummary } from '@uncain/shared';
import { Link } from 'react-router';

interface FindingRow {
	readonly detectorId: string;
	readonly threatClass: string;
	readonly evidenceTier: string;
	readonly finding: string;
	readonly recommendedAction: string;
	readonly affectedPaths: readonly string[];
	readonly analysisPath?: string;
}

export function PosturePage() {
	const { data: summary, isLoading: summaryLoading } = useQuery<PostureSummary>({
		queryKey: queryKeys.posture.summary(),
		queryFn: () => get<PostureSummary>('/posture/summary'),
	});

	const { data: reposData, isLoading: reposLoading } = useQuery<{
		repositories: PostureRepository[];
	}>({
		queryKey: queryKeys.posture.repositories(),
		queryFn: () => get<{ repositories: PostureRepository[] }>('/posture/repositories'),
	});

	const { data: findingsData } = useQuery<{ findings: FindingRow[] }>({
		queryKey: queryKeys.posture.findings(),
		queryFn: () => get<{ findings: FindingRow[] }>('/posture/findings'),
	});

	if (summaryLoading || reposLoading) {
		return <div className="text-muted-foreground">Loading posture data...</div>;
	}

	const repos = reposData?.repositories ?? [];
	const findings = findingsData?.findings ?? [];

	return (
		<div className="space-y-8">
			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-bold text-foreground">Posture Overview</h1>
				<SyncButton />
			</div>

			{summary && <PostureSummaryCards summary={summary} findingCount={findings.length} />}

			<section className="space-y-3">
				<div>
					<h2 className="text-lg font-semibold text-foreground">Trust Graph Explorer</h2>
					<p className="text-sm text-muted-foreground">
						Investigate repository, workflow, and dependency relationships with live finding
						context.
					</p>
				</div>
				<TrustGraphPanel query={{ scope: 'org' }} />
			</section>

			{findings.length > 0 && (
				<div>
					<h2 className="mb-4 text-lg font-semibold text-foreground">
						Findings ({findings.length})
					</h2>
					<div className="space-y-3">
						{findings.map((f, i) => (
							<FindingCard key={`${f.detectorId}-${String(i)}`} finding={f} repos={repos} />
						))}
					</div>
				</div>
			)}

			<div>
				<h2 className="mb-4 text-lg font-semibold text-foreground">Repositories</h2>
				{repos.length === 0 ? (
					<div className="rounded-lg border border-border bg-card p-8 text-center text-muted-foreground">
						No repositories scanned yet.
					</div>
				) : (
					<div className="space-y-3">
						{repos.map((repo) => (
							<RepoCard key={repo.id} repo={repo} />
						))}
					</div>
				)}
			</div>
		</div>
	);
}

function PostureSummaryCards({
	summary,
	findingCount,
}: { summary: PostureSummary; findingCount: number }) {
	const cards = [
		{ label: 'Repositories', value: summary.repoCount },
		{ label: 'Workflows', value: summary.workflowCount },
		{ label: 'Actions', value: summary.actionCount },
		{ label: 'Findings', value: findingCount, alarm: true },
		{ label: 'Quarantined', value: summary.quarantineCount, alarm: true },
	];

	return (
		<div className="grid grid-cols-2 gap-4 md:grid-cols-5">
			{cards.map((card) => (
				<div key={card.label} className="rounded-lg border border-border bg-card p-4 text-center">
					<p
						className={cn(
							'text-3xl font-bold',
							card.alarm && card.value > 0 ? 'text-severity-critical' : 'text-foreground',
						)}
					>
						{card.value}
					</p>
					<p className="text-xs text-muted-foreground">{card.label}</p>
				</div>
			))}
		</div>
	);
}

function RepoCard({ repo }: { repo: PostureRepository }) {
	return (
		<Link
			to={`/posture/repos/${repo.id}`}
			className="block rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/30"
		>
			<div className="flex items-center justify-between">
				<div>
					<h3 className="font-medium text-foreground">{repo.fullName}</h3>
					<p className="text-xs text-muted-foreground">
						{repo.workflowCount} workflows · {repo.visibility} · {repo.defaultBranch}
					</p>
				</div>
				<div className="text-right">
					{repo.findingCount > 0 ? (
						<span className="text-xs font-medium text-severity-high">
							{repo.findingCount} findings
						</span>
					) : (
						<span className="text-xs text-green-600">No findings</span>
					)}
				</div>
			</div>
		</Link>
	);
}

const ACTION_COLORS: Record<string, string> = {
	quarantine: 'text-severity-critical',
	cancel_runs: 'text-severity-high',
	alert: 'text-severity-medium',
	observe: 'text-severity-low',
};

function FindingCard({ finding, repos }: { finding: FindingRow; repos: PostureRepository[] }) {
	const paths = finding.affectedPaths;

	// Match a path to a repo from the repos list
	function findRepoForPath(path: string): { repo: PostureRepository; workflowPath: string } | null {
		for (const r of repos) {
			if (path.startsWith(`${r.fullName}/`)) {
				return { repo: r, workflowPath: path.slice(r.fullName.length + 1) };
			}
		}
		return null;
	}

	return (
		<div className="rounded-lg border border-border bg-card p-4">
			<div className="flex items-start justify-between gap-4">
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2">
						<span className="font-mono text-sm font-medium text-foreground">
							{finding.detectorId}
						</span>
						<span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
							{THREAT_CLASS_LABELS[finding.threatClass] ?? finding.threatClass}
						</span>
						<span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
							Tier {finding.evidenceTier}
						</span>
						{finding.analysisPath && finding.analysisPath !== 'hot' && (
							<span
								className={`rounded-full px-2 py-0.5 text-xs font-medium ${ANALYSIS_PATH_COLORS[finding.analysisPath] ?? 'bg-gray-100 text-gray-800'}`}
							>
								{ANALYSIS_PATH_LABELS[finding.analysisPath] ?? finding.analysisPath}
							</span>
						)}
					</div>
					<p className="mt-1 text-sm text-muted-foreground">{finding.finding}</p>
					{paths.length > 0 && (
						<div className="mt-2 flex flex-wrap gap-1">
							{paths.map((p) => {
								const match = findRepoForPath(p);
								if (match) {
									return (
										<Link
											key={p}
											to={`/posture/repos/${match.repo.id}?workflowPath=${encodeURIComponent(match.workflowPath)}`}
											className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-xs text-primary hover:bg-primary/20"
										>
											{match.repo.fullName} › {match.workflowPath}
										</Link>
									);
								}
								return (
									<span
										key={p}
										className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground"
									>
										{p}
									</span>
								);
							})}
						</div>
					)}
				</div>
				<span
					className={cn(
						'whitespace-nowrap text-xs font-medium',
						ACTION_COLORS[finding.recommendedAction] ?? 'text-muted-foreground',
					)}
				>
					{finding.recommendedAction}
				</span>
			</div>
		</div>
	);
}

// ── SyncButton ──────────────────────────────────────────────────────────

function SyncButton() {
	const { data: user } = useCurrentUser();
	const { data: syncState } = useSyncState();
	const triggerSync = useSyncTrigger();

	const isOperatorOrAbove =
		user?.currentOrg?.role === 'operator' || user?.currentOrg?.role === 'admin';
	const isRunning = syncState?.status === 'running';
	const isCooldown = triggerSync.error?.message?.includes('cooldown');

	const handleSync = () => {
		if (!isRunning && isOperatorOrAbove) {
			triggerSync.mutate();
		}
	};

	return (
		<div className="flex items-center gap-3">
			{syncState?.lastSyncCompletedAt && (
				<span className="text-xs text-muted-foreground">
					Last synced{' '}
					<span className={cn(SYNC_STATUS_COLORS[syncState.status] ?? 'text-muted-foreground')}>
						{formatRelativeTime(syncState.lastSyncCompletedAt)}
					</span>
				</span>
			)}
			{syncState?.status === 'completed' && syncState.diff && (
				<span className="text-xs text-muted-foreground">
					({syncState.diff.findingsDelta > 0 ? '+' : ''}
					{syncState.diff.findingsDelta} findings)
				</span>
			)}
			{isOperatorOrAbove && (
				<button
					type="button"
					onClick={handleSync}
					disabled={isRunning || triggerSync.isPending || isCooldown}
					className={cn(
						'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
						isRunning || triggerSync.isPending
							? 'cursor-not-allowed bg-blue-100 text-blue-700'
							: isCooldown
								? 'cursor-not-allowed bg-muted text-muted-foreground'
								: 'bg-primary text-primary-foreground hover:bg-primary/90',
					)}
				>
					{isRunning || triggerSync.isPending ? (
						<span className="flex items-center gap-1.5">
							<span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-blue-700 border-t-transparent" />
							{SYNC_STATUS_LABELS.running}
						</span>
					) : syncState?.status === 'failed' ? (
						'Retry Sync'
					) : (
						'Sync Now'
					)}
				</button>
			)}
		</div>
	);
}
